import { API_BASE } from './api.js';
import { deleteChunk, pendingUpload, saveChunk, type StoredChunk } from './chunkStore.js';
import { clientLog } from './clientLog.js';

export interface ChunkQueueState {
  uploaded: number;
  pending: number;
  failed: number;
  /** True while a chunk has exhausted its fast retries and is being retried slowly. */
  stalled: boolean;
  online: boolean;
  lastError: string | null;
}

interface QueuedChunk {
  index: number;
  blob: Blob;
  attempts: number;
  /**
   * Read once, reused by every retry. iOS Safari's fetch() throws "Load failed"
   * on a Blob body straight from MediaRecorder; an ArrayBuffer does not.
   */
  body?: ArrayBuffer;
}

/** After this many tries the chunk is not dropped — it is retried at MAX_DELAY_MS. */
const STALL_AFTER_ATTEMPTS = 8;
/** Fast retries first: a transient iOS fetch failure clears in under a second. */
const RETRY_DELAYS_MS = [500, 1_000, 2_000, 4_000, 8_000];
const MAX_DELAY_MS = 15_000;
/** A request that never settles used to hold the whole queue behind it. */
const REQUEST_TIMEOUT_MS = 60_000;

export class CompleteMissingChunksError extends Error {
  constructor(public readonly missing: number[]) {
    super(`server is missing chunks: ${missing.join(', ')}`);
    this.name = 'CompleteMissingChunksError';
  }
}

/**
 * Uploads recording chunks one at a time, in order. A chunk is never thrown
 * away: once the fast retries are spent the queue keeps trying at the capped
 * backoff and reports `stalled` so the recorder can say so. Every chunk is
 * mirrored into IndexedDB until the server has acknowledged it.
 */
export class ChunkQueue {
  private queue: QueuedChunk[] = [];
  private running = false;
  private stopped = false;
  /** Set once the server owns the whole take; nothing more may be uploaded. */
  private completed = false;
  private uploaded = 0;
  private stalled = false;
  private lastError: string | null = null;
  private stopRequestedAt: number | null = null;
  private drainReported = false;
  /** Kept so a 409 missing_chunks can be answered without touching the disk. */
  private readonly sent = new Map<number, Blob>();

  constructor(
    private readonly meetingId: string,
    private readonly attempt: number,
    private readonly mimeType: string,
    private readonly onChange: (state: ChunkQueueState) => void,
  ) {
    window.addEventListener('online', this.handleOnline);
  }

  /** Rebuilds a queue from chunks an earlier session left in IndexedDB. */
  static async resume(
    meetingId: string,
    onChange: (state: ChunkQueueState) => void,
  ): Promise<{ queue: ChunkQueue; chunks: StoredChunk[] } | null> {
    const pending = await pendingUpload(meetingId);
    if (!pending || pending.chunks.length === 0) return null;
    const mimeType = pending.chunks[0]!.mimeType;
    const queue = new ChunkQueue(meetingId, pending.attempt, mimeType, onChange);
    for (const chunk of pending.chunks) queue.enqueue(chunk.index, chunk.blob);
    return { queue, chunks: pending.chunks };
  }

  private handleOnline = (): void => {
    this.emit();
    void this.drain();
  };

  private emit(): void {
    this.onChange({
      uploaded: this.uploaded,
      pending: this.queue.length,
      failed: 0,
      stalled: this.stalled,
      online: navigator.onLine,
      lastError: this.lastError,
    });
  }

  private log(event: string, detail: Record<string, unknown>, level: 'info' | 'warn' | 'error'): void {
    clientLog(event, detail, { level, meetingId: this.meetingId });
  }

  get uploadedCount(): number {
    return this.uploaded;
  }

  get pendingCount(): number {
    return this.queue.length;
  }

  get attemptNumber(): number {
    return this.attempt;
  }

  get lastErrorText(): string | null {
    return this.lastError;
  }

  /** Marks the moment Stop was pressed, so the drain can report how long it took. */
  markStopping(): void {
    if (this.stopRequestedAt === null) this.stopRequestedAt = Date.now();
  }

  /** Persists the chunk first, so a crash between here and the upload is survivable. */
  push(index: number, blob: Blob): void {
    this.sent.set(index, blob);
    void saveChunk({
      meetingId: this.meetingId,
      attempt: this.attempt,
      index,
      blob,
      mimeType: this.mimeType,
    });
    this.enqueue(index, blob);
  }

  private isQueued(index: number): boolean {
    return this.queue.some((c) => c.index === index);
  }

  private enqueue(index: number, blob: Blob): void {
    this.sent.set(index, blob);
    // The server already has the whole take; a late chunk would only 409.
    if (this.completed) {
      this.log('chunk.straggler.dropped', { index, size: blob.size }, 'warn');
      return;
    }
    // A 409 re-send used to queue an index that was already waiting, which
    // uploaded the same chunk twice.
    if (this.isQueued(index)) return;
    this.queue.push({ index, blob, attempts: 0 });
    this.emit();
    void this.drain();
  }

  private async putChunk(chunk: QueuedChunk): Promise<void> {
    // Read once and cache: retries must not re-read the Blob, and WebKit only
    // reliably uploads a detached ArrayBuffer.
    chunk.body ??= await chunk.blob.arrayBuffer();
    const res = await fetch(
      `${API_BASE}/meetings/${this.meetingId}/recording/chunks/${chunk.index}`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': this.mimeType },
        body: chunk.body,
        keepalive: false,
        signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
      },
    );
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    await deleteChunk(this.meetingId, this.attempt, chunk.index);
  }

  private async drain(): Promise<void> {
    if (this.running || this.stopped || this.completed) return;
    this.running = true;
    try {
      while (this.queue.length > 0 && !this.stopped && !this.completed) {
        const chunk = this.queue[0]!;
        if (!navigator.onLine) {
          this.emit();
          await delay(2000);
          continue;
        }
        try {
          await this.putChunk(chunk);
          this.queue.shift();
          this.uploaded += 1;
          this.lastError = null;
          if (this.queue.length === 0) this.stalled = false;
          this.emit();
        } catch (err) {
          chunk.attempts += 1;
          const error = err instanceof Error ? err : new Error(String(err));
          this.lastError = error.message;
          this.log(
            'chunk.put.failed',
            {
              index: chunk.index,
              attempt: chunk.attempts,
              errorName: error.name,
              errorMessage: error.message,
              size: chunk.blob.size,
              mimeType: this.mimeType,
              online: navigator.onLine,
              visibility: typeof document === 'undefined' ? null : document.visibilityState,
            },
            'error',
          );
          // Audio is never discarded: past the fast retries we simply slow down.
          if (chunk.attempts >= STALL_AFTER_ATTEMPTS && !this.stalled) {
            this.stalled = true;
            this.log(
              'chunk.stalled',
              { index: chunk.index, attempt: chunk.attempts, errorMessage: error.message },
              'error',
            );
          }
          this.emit();
          await delay(RETRY_DELAYS_MS[chunk.attempts - 1] ?? MAX_DELAY_MS);
        }
      }
      this.reportDrained();
    } finally {
      this.running = false;
      this.emit();
    }
  }

  /** One line per stop saying how long the tail of the upload actually took. */
  private reportDrained(): void {
    if (this.drainReported || this.stopRequestedAt === null) return;
    if (this.queue.length > 0) return;
    this.drainReported = true;
    this.log(
      'chunk.drain.completed',
      { uploaded: this.uploaded, elapsedMs: Date.now() - this.stopRequestedAt },
      'info',
    );
  }

  /** Resolves once the queue is empty or the timeout expires. */
  async flush(timeoutMs = 120_000): Promise<boolean> {
    const deadline = Date.now() + timeoutMs;
    void this.drain();
    while (this.queue.length > 0 && Date.now() < deadline && !this.stopped && !this.completed) {
      await delay(300);
      void this.drain();
    }
    return this.queue.length === 0;
  }

  private async postComplete(
    totalChunks: number,
    durationSec: number,
  ): Promise<{ ok: true; alreadyComplete?: boolean }> {
    const res = await fetch(`${API_BASE}/meetings/${this.meetingId}/recording/complete`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ totalChunks, durationSec }),
    });
    const body = (await res.json().catch(() => ({}))) as {
      error?: string;
      missing?: number[];
      alreadyComplete?: boolean;
    };
    if (res.status === 409 && body.error === 'missing_chunks') {
      throw new CompleteMissingChunksError(body.missing ?? []);
    }
    if (!res.ok) throw new Error(body.error ?? `HTTP ${res.status}`);
    return { ok: true, ...(body.alreadyComplete ? { alreadyComplete: true } : {}) };
  }

  /**
   * Closes the recording. If the server reports gaps, the named chunks are
   * re-sent from the copies still in memory and the complete is retried.
   */
  async complete(
    totalChunks: number,
    durationSec: number,
    retries = 2,
  ): Promise<{ ok: true; alreadyComplete?: boolean }> {
    for (let attempt = 0; ; attempt += 1) {
      try {
        const result = await this.postComplete(totalChunks, durationSec);
        this.markCompleted();
        return result;
      } catch (err) {
        if (!(err instanceof CompleteMissingChunksError) || attempt >= retries) throw err;
        this.log('chunk.complete.missing', { attempt, missing: err.missing, totalChunks }, 'warn');
        const resendable = err.missing.filter((i) => this.sent.has(i) && !this.isQueued(i));
        if (resendable.length === 0) throw err;
        this.lastError = null;
        for (const index of resendable) this.enqueue(index, this.sent.get(index)!);
        await this.flush();
      }
    }
  }

  /** The audio is on the server now; anything still queued is a duplicate. */
  private markCompleted(): void {
    this.completed = true;
    if (this.queue.length > 0) {
      this.log(
        'chunk.straggler.dropped',
        { indexes: this.queue.map((c) => c.index) },
        'warn',
      );
      this.queue = [];
    }
    this.stalled = false;
    this.reportDrained();
    this.emit();
  }

  /** Called once the server owns the audio; drops the local copies. */
  async discardPersisted(): Promise<void> {
    for (const index of this.sent.keys()) {
      await deleteChunk(this.meetingId, this.attempt, index);
    }
    this.sent.clear();
  }

  dispose(): void {
    this.stopped = true;
    window.removeEventListener('online', this.handleOnline);
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
